import React, { useState } from "react"

const csrfToken = () => document.querySelector('meta[name="csrf-token"]')?.content

const STEPS = ["ga4", "cloud_project", "bq_link", "grant", "connect"]

const primaryBtn =
  "inline-flex items-center justify-center rounded-lg bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-brand-700 active:scale-95 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
const ghostBtn =
  "inline-flex items-center justify-center rounded-lg border border-gray-200 dark:border-gray-800 px-4 py-2.5 text-sm font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 active:scale-95 transition-all disabled:opacity-60"
const inputCls =
  "w-full rounded-lg border border-gray-300 dark:border-gray-700 px-3 py-2 text-sm focus:border-brand-500 focus:ring-brand-500"

// Stateful, resumable wizard. The server owns `setup_step`; each action PATCHes
// /bigquery_connection with an `op` and re-renders from the returned state, so a
// partner can reload mid-setup and land on the same step. Live checks (grant,
// connect) probe real Google state and only advance on success.
export function BigquerySetupWizard({ connectionPath, connection, setConnection, copy, onClose }) {
  const [project, setProject] = useState(connection.gcp_project_id || "")
  const [dataset, setDataset] = useState(connection.dataset_id || "")
  const [datasets, setDatasets] = useState([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const [copied, setCopied] = useState(false)
  const [editingProject, setEditingProject] = useState(false)

  const step = connection.setup_step
  const wc = copy.wizard
  const sc = wc.steps

  const patch = async (body) => {
    setBusy(true)
    setError(null)
    try {
      const res = await fetch(connectionPath, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken(),
          Accept: "application/json",
        },
        body: JSON.stringify(body),
      })
      const data = await res.json()
      if (res.ok) {
        setConnection((prev) => ({ ...prev, ...data }))
        if (data.datasets) setDatasets(data.datasets)
        return true
      }
      setError((data.errors && data.errors[0]) || copy.errorGeneric)
      if (data.status) setConnection((prev) => ({ ...prev, ...data }))
      return false
    } catch {
      setError(copy.errorGeneric)
      return false
    } finally {
      setBusy(false)
    }
  }

  const advance = (setupStep) => patch({ op: "advance", setup_step: setupStep })
  const saveProject = () => patch({ op: "save_project", bigquery_connection: { gcp_project_id: project } })
  const saveProjectEdit = async () => {
    if (await saveProject()) setEditingProject(false)
  }
  const verifyGrant = () => patch({ op: "verify_grant" })
  const connect = () => patch({ op: "connect", bigquery_connection: { dataset_id: dataset } })

  const copySa = () => {
    navigator.clipboard?.writeText(connection.service_account_email || "")
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }

  const idx = STEPS.indexOf(step)

  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50 p-5">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">{wc.title}</h3>
        <button type="button" onClick={onClose} className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">
          {wc.close}
        </button>
      </div>

      <p className="mt-2 rounded-lg bg-amber-50 dark:bg-amber-950/40 px-3 py-2 text-xs text-amber-800 dark:text-amber-300">{wc.waitNotice}</p>

      {/* Step rail */}
      <ol className="mt-4 flex flex-wrap gap-2 text-xs">
        {STEPS.map((s, i) => (
          <li
            key={s}
            className={`rounded-full px-2 py-0.5 ${
              i < idx
                ? "bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300"
                : i === idx
                  ? "bg-brand-100 dark:bg-brand-900/40 text-brand-700 dark:text-brand-300"
                  : "bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500"
            }`}
          >
            {i + 1}. {sc[s].title}
          </li>
        ))}
      </ol>

      <div className="mt-4 space-y-3">
        <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{sc[step]?.title}</h4>
        <p className="text-sm text-gray-600 dark:text-gray-400">{sc[step]?.body}</p>

        {Array.isArray(sc[step]?.details) && sc[step].details.length > 0 && (
          <ol className="list-decimal space-y-1.5 rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 px-5 py-3 text-sm text-gray-600 dark:text-gray-400 marker:text-gray-400">
            {sc[step].details.map((d, i) => (
              <li key={i} className="pl-1">
                {d}
              </li>
            ))}
          </ol>
        )}

        {sc[step]?.note && (
          <p className="rounded-lg border border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-950/40 px-3 py-2 text-xs text-amber-800 dark:text-amber-300">
            {sc[step].note}
          </p>
        )}

        {sc[step]?.docUrl && (
          <a
            href={sc[step].docUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={`${ghostBtn} w-full sm:w-auto`}
          >
            {sc[step].docLabel} ↗
          </a>
        )}

        {step === "ga4" && (
          <button type="button" onClick={() => advance("cloud_project")} disabled={busy} className={primaryBtn}>
            {sc.ga4.cta}
          </button>
        )}

        {step === "cloud_project" && (
          <div className="space-y-2">
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">{sc.cloud_project.projectLabel}</label>
            <input
              type="text"
              value={project}
              onChange={(e) => setProject(e.target.value)}
              placeholder={sc.cloud_project.projectPlaceholder}
              className={inputCls}
            />
            <button type="button" onClick={saveProject} disabled={busy || !project.trim()} className={primaryBtn}>
              {sc.cloud_project.cta}
            </button>
          </div>
        )}

        {step === "bq_link" && (
          <button type="button" onClick={() => advance("grant")} disabled={busy} className={primaryBtn}>
            {sc.bq_link.cta}
          </button>
        )}

        {step === "grant" && (
          <div className="space-y-2">
            {/* Let the partner fix a wrong/typo'd Project ID without restarting. */}
            <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 px-3 py-2 text-xs">
              {editingProject ? (
                <div className="space-y-2">
                  <label className="block font-medium text-gray-700 dark:text-gray-300">{wc.currentProject}</label>
                  <input
                    type="text"
                    value={project}
                    onChange={(e) => setProject(e.target.value)}
                    placeholder={sc.cloud_project.projectPlaceholder}
                    className={inputCls}
                  />
                  <div className="flex gap-2">
                    <button type="button" onClick={saveProjectEdit} disabled={busy || !project.trim()} className={primaryBtn}>
                      {wc.saveProject}
                    </button>
                    <button type="button" onClick={() => setEditingProject(false)} className={ghostBtn}>
                      {wc.close}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between gap-2">
                  <span className="text-gray-600 dark:text-gray-400">
                    {wc.currentProject}: <code className="text-gray-900 dark:text-gray-100">{connection.gcp_project_id}</code>
                  </span>
                  <button
                    type="button"
                    onClick={() => setEditingProject(true)}
                    className="font-medium text-brand-600 dark:text-brand-400 hover:text-brand-700"
                  >
                    {wc.editProject}
                  </button>
                </div>
              )}
            </div>

            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">{sc.grant.saLabel}</label>
            <div className="flex items-center gap-2">
              <code className="flex-1 truncate rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-xs">
                {connection.service_account_email}
              </code>
              <button type="button" onClick={copySa} className={ghostBtn}>
                {copied ? wc.copied : wc.copy}
              </button>
            </div>
            <button type="button" onClick={verifyGrant} disabled={busy} className={primaryBtn}>
              {busy ? wc.checking : wc.check}
            </button>
          </div>
        )}

        {step === "connect" && (
          <div className="space-y-2">
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">{sc.connect.datasetLabel}</label>
            {datasets.length > 0 ? (
              <select value={dataset} onChange={(e) => setDataset(e.target.value)} className={inputCls}>
                <option value="">—</option>
                {datasets.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                value={dataset}
                onChange={(e) => setDataset(e.target.value)}
                placeholder="analytics_123456789"
                className={inputCls}
              />
            )}
            <button type="button" onClick={connect} disabled={busy || !dataset.trim()} className={primaryBtn}>
              {busy ? wc.checking : sc.connect.cta}
            </button>
          </div>
        )}

        {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
      </div>
    </div>
  )
}
